/**
 * SDK stream-event decoder — the pure function that turns raw Claude Agent
 * SDK messages into Zustand store mutations + runtime-state bookkeeping.
 *
 * Extracted from useCoworkSession._processStreamMessage so:
 *   - The 500-line switch has its own file and can evolve per-message-type
 *   - It's reusable by any future caller (e.g. disk-reload to CortexMessage)
 *   - The hook that uses it no longer needs to re-declare 10 callback deps
 *     on every render
 *
 * Invariant: this module has NO React state of its own. All side effects
 * flow through the ctx argument — store writes via ctx.store, UI feedback
 * via ctx.api/ctx.setHitl/ctx.backgroundEvent, runtime-struct updates via
 * ctx.runtimes. That means the decoder is synchronously replayable for
 * tests + can be called from any context (live stream, disk-reload, etc).
 */

import type { ContentBlock, ToolUseBlock, HITLState, TodoItem, WorkflowRun, WorkflowPhase, WorkflowAgent } from './sessionModel'
import type { CoworkSessionStore, StreamDecoderContext } from './decoderHost'
import { friendlyError, errorActionKind } from './friendlyError'
import { seedHash } from './seedHash'
import { settleInProgressTodos } from './todoState'
import { parseWorkflowProgressArray, terminalWorkflowStatus } from './workflowState'
import { applySubagentRetryProgress } from './subagentRetry'
import { resolveHitlPrompt } from './hitlQueue'

export type { StreamDecoderContext } from './decoderHost'
export { parseWorkflowProgressArray, terminalWorkflowStatus } from './workflowState'

/** Upsert a workflow run into the session-level registry that powers the
 *  Background-tasks panel. Only creates on task_started (patch carries `name`);
 *  later task_progress/updated/notification events merge into the existing run.
 *  This registry is the SINGLE SOURCE OF TRUTH for a workflow: both the inline
 *  chip (via the tool_use block's taskId) and the Background-tasks panel read
 *  from it. A workflow is a session-scoped background task whose events outlive
 *  the launching turn, so its state must NOT be anchored to any turn's message. */
function upsertSessionWorkflow(
  store: CoworkSessionStore,
  sid: string,
  taskId: string | undefined,
  patch: Partial<WorkflowRun>,
): void {
  if (!taskId) return
  const cur = store.getState().sessions[sid]?.workflows || {}
  const existing = cur[taskId]
  if (!existing && !patch.name) return
  store.getState().updateSession(sid, {
    workflows: { ...cur, [taskId]: { ...existing, ...patch, taskId } as WorkflowRun },
  })
}

/** Settle a workflow to a terminal state: flip its status AND coerce any still-
 *  running agents to terminal (so the chip rollup reads N/N and no agent spins
 *  after completion — the final workflow_progress snapshot can be debounced away,
 *  leaving agents mid-flight). Writes only the session registry. */
function settleWorkflowTerminal(
  store: CoworkSessionStore,
  sid: string,
  taskId: string,
  status: 'completed' | 'failed' | 'stopped',
  extra?: Partial<WorkflowRun>,
): void {
  const run = store.getState().sessions[sid]?.workflows?.[taskId]
  // Terminal state for agents still in flight when the workflow settles. A STOPPED
  // workflow (user killed it) did NOT fail its in-flight agents — mark them neutral
  // 'stopped', not 'error' (red), so a manual stop doesn't read as a crash.
  const inflightState: WorkflowAgent['state'] =
    status === 'completed' ? 'done' : status === 'stopped' ? 'stopped' : 'error'
  const agents = (run?.agents ?? []).map((a) => {
    const s = String(a.state || '').toLowerCase()
    return s === 'done' || s === 'error'
      ? a
      : { ...a, state: inflightState }
  })
  upsertSessionWorkflow(store, sid, taskId, { status, agents, ...extra })

  // The workflow normally wakes the agent for a resume turn, but that resume
  // event can be dropped while the durable workflow terminal event still
  // arrives. Once every background workflow is terminal and the session is
  // idle, settle the turn-scoped todo as well so it cannot spin forever.
  const session = store.getState().sessions[sid]
  const anyWorkflowRunning = Object.values(session?.workflows ?? {})
    .some((workflow) => workflow.status === 'running')
  if (session && !session.processIsRunning && !anyWorkflowRunning) {
    const todos = settleInProgressTodos(session.todos, status === 'failed' ? 'failed' : 'paused')
    if (todos !== session.todos) store.getState().updateSession(sid, { todos })
  }
}

/**
 * Isolate the read of the UNDECLARED `workflow_progress` field. The native SDK
 * binary (claude-agent-sdk-darwin-arm64) hangs this off `task_progress`, but the
 * JS d.ts (v0.3.198) does not declare it — so it's read via `as any` here and
 * NOWHERE else. Shape-validate + return null on mismatch: a binary reshape then
 * degrades gracefully to the plain-usage path (token/duration counters) instead
 * of throwing or blanking the card. `workflow_progress` is the FULL snapshot each
 * tick (latest-wins, keyed by type:index server-side), so consumers overwrite
 * phases/agents wholesale. Parsing itself lives in the DOM-free `workflowState`
 * module (shared with the main-process reloader).
 */
function decodeWorkflowProgress(msg: unknown): { phases: WorkflowPhase[]; agents: WorkflowAgent[] } | null {
  return parseWorkflowProgressArray((msg as { workflow_progress?: unknown })?.workflow_progress)
}

/**
 * turn 收尾兜底：把仍 `running` 的 tool_use block（尤其 Agent 块——它故意不在
 * content_block_stop 完成、靠 tool_result 收尾）连同其 subagent steps 一律标
 * `completed`，否则卡片的 spinner 会一直转。两条收尾路径共用：result handler
 * （自然结束 / 出错）和 useCoworkSession.cancelStream（用户手动中断）。
 */
export function completeRunningToolBlocks(
  blocks: ContentBlock[] | undefined,
): ContentBlock[] | undefined {
  return blocks?.map((b) =>
    b.type === 'tool_use' && b.status === 'running'
      ? {
        ...b,
        status: 'completed' as const,
        duration: b.duration ?? (b.startedAt ? Date.now() - b.startedAt : undefined),
        steps: b.steps?.map((st) => st.status === 'running' ? { ...st, status: 'completed' as const, duration: st.duration ?? (Date.now() - st.startedAt) } : st),
      }
      : b,
  )
}

// ── HITL dedupe cache (module-level so it survives hook re-renders) ──
// Nonce = stable hash over (sessionId, requirements, rawQuestions).
// Cache TTL mirrors Claude Desktop's plugin-shim response-sweep window.
const HITL_DECISION_TTL_MS = 5000
const hitlDecisionCache = new Map<string, { answers: Record<string, string>; ts: number }>()

export function hitlNonce({ sessionId, requirements, rawQuestions }: Pick<HITLState, 'sessionId' | 'requirements' | 'rawQuestions'>): string {
  return seedHash(JSON.stringify([sessionId, requirements, rawQuestions])).toString(16)
}

export function cacheHitlDecision(nonce: string, answers: Record<string, string>): void {
  // Prune stale entries on write — keeps the module-scope Map bounded without
  // a timer. Small N (<10 concurrent approvals typical), so O(n) scan is fine.
  const now = Date.now()
  for (const [k, v] of hitlDecisionCache) {
    if (now - v.ts > HITL_DECISION_TTL_MS) hitlDecisionCache.delete(k)
  }
  hitlDecisionCache.set(nonce, { answers, ts: now })
}

function cachedHitlDecision(nonce: string): Record<string, string> | null {
  const hit = hitlDecisionCache.get(nonce)
  if (!hit) return null
  if (Date.now() - hit.ts > HITL_DECISION_TTL_MS) { hitlDecisionCache.delete(nonce); return null }
  return hit.answers
}

// ── Task 工具 → session.todos ──────────────────────────────────────────
// SDK 0.3.142+ 用 TaskCreate / TaskUpdate / TaskList 取代 TodoWrite 的整表
// 快照。消费方按 task id 增量累积：
//   - TaskCreate：id 只在工具结果里。SDK 的 tool_result 是**纯文本**
//     "Task #<id> created successfully: <subject>"（不是 JSON）—— 必须从文本
//     解析。subject 也在 input 里兜底。
//   - TaskUpdate：按 taskId patch 单条；status:'deleted' 删除该条（全在 input）
//   - TaskList：结果是全量快照文本，逐行 "#<id> [<status>] <subject>" resync
// id = run of non-space/non-colon chars (keeps dotted/sub-ids intact; `\b` would
// truncate "1.2" → "1"). TaskList line is anchored to the line start so prose
// like "see #3 [pending] note" inside a subject can't masquerade as a row.
const TASK_CREATE_RE = /Task #([^\s:]+).*?:\s*([\s\S]*)/
const TASK_LIST_LINE_RE = /^\s*#([^\s\]]+).*?\[(pending|in_progress|completed)\]\s*(.*)/
// Display in task-id order. Ids are assigned sequentially at create time, so id
// order == creation order == the phase order the agent intends. TaskCreate
// tool_results can arrive out of order (batched creates), so appending by arrival
// scrambles the list — sort by numeric id (string fallback) to keep it stable.
const byTaskId = (a: { id?: string }, b: { id?: string }): number => {
  // empty id is falsy → NaN (Number('') === 0 would sort blanks ahead of #1)
  const na = a.id ? Number(a.id) : NaN, nb = b.id ? Number(b.id) : NaN
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
  return String(a.id ?? '').localeCompare(String(b.id ?? ''))
}
/**
 * 流里的 TaskUpdate input 是模型的原样输出:官方文档明说 host 会修正一部分
 * 「接近但不对」的键名(id / task_id → taskId),而**修正不反映在流上**。
 * 消费流的一侧只能自己容错,否则模型偶尔写错键就静默丢一次状态更新。
 */
function normalizeTaskUpdate(input: any): any {
  const taskId = input?.taskId ?? input?.id ?? input?.task_id
  return taskId ? { ...input, taskId } : input
}

export function applyTaskTool(
  store: CoworkSessionStore,
  sid: string,
  toolName: string,
  input: any,
  rawOutput?: string,
): void {
  const st = store.getState()
  const cur = st.sessions[sid]?.todos ?? []
  if (toolName === 'TaskCreate') {
    // 先试 JSON（非 Cortex host 可能返回 {task:{id,subject}}），失败回落到文本
    let id: string | undefined
    let subject: string | undefined
    if (rawOutput) {
      try {
        const j = JSON.parse(rawOutput)
        if (j?.task?.id) { id = String(j.task.id); subject = j.task.subject }
      } catch { /* not JSON — fall through to text */ }
      // JSON absent OR present-but-not-a-task → try the plain-text shape too,
      // so a host that returns {ok:true} (no task) doesn't silently drop the id.
      if (!id) {
        const m = rawOutput.match(TASK_CREATE_RE)
        if (m) { id = m[1]; subject = m[2].trim() }
      }
    }
    if (!id) {
      console.warn('[TaskCreate] could not parse task id from result', rawOutput?.slice(0, 120))
      return
    }
    if (cur.some(t => t.id === id)) return
    st.updateSession(sid, { todos: [...cur, {
      id,
      content: (subject || input?.subject || '') as string,
      status: 'pending' as const,
      activeForm: input?.activeForm,
    }].sort(byTaskId) })
  } else if (toolName === 'TaskUpdate') {
    const taskId = input?.taskId
    if (!taskId) return
    if (input.status === 'deleted') {
      st.updateSession(sid, { todos: cur.filter(t => t.id !== taskId) })
      return
    }
    st.updateSession(sid, { todos: cur.map(t => t.id === taskId ? {
      ...t,
      status: (input.status ?? t.status) as TodoItem['status'],
      content: (input.subject ?? t.content) as string,
      activeForm: input.activeForm ?? t.activeForm,
    } : t) })
  } else if (toolName === 'TaskList') {
    if (!rawOutput) return
    // JSON 优先；否则按行解析文本快照
    let tasks: { id: string; content: string; status: TodoItem['status'] }[] | undefined
    let fromJson = false
    try {
      const j = JSON.parse(rawOutput)
      if (Array.isArray(j?.tasks)) {
        fromJson = true
        tasks = j.tasks.map((t: any) => ({ id: String(t.id), content: t.subject, status: t.status }))
      }
    } catch {
      const lines = rawOutput.split('\n').map(l => l.match(TASK_LIST_LINE_RE)).filter(Boolean) as RegExpMatchArray[]
      if (lines.length) tasks = lines.map(m => ({ id: m[1], content: m[3].trim(), status: m[2] as TodoItem['status'] }))
    }
    if (fromJson) {
      // Authoritative full snapshot — apply even when empty so a cleared list
      // (all tasks deleted) actually clears the panel instead of going stale.
      st.updateSession(sid, { todos: (tasks ?? []).slice().sort(byTaskId) })
    } else if (tasks && tasks.length) {
      st.updateSession(sid, { todos: tasks.slice().sort(byTaskId) })
    } else if (rawOutput.includes('#')) {
      // Looked like it had rows but none parsed — don't clobber a good list with nothing.
      console.warn('[TaskList] snapshot had task-like text but none parsed; keeping existing todos', rawOutput.slice(0, 120))
    }
  }
}

// eslint-disable-next-line complexity
export function decodeStreamMessage(
  msg: any,
  assistantMsgId: string,
  targetSid: string,
  ctx: StreamDecoderContext,
): void {
  const { store, runtimes, hitlQueueRef, appendText, batchUpdate, friendlyToolName, computeToolLabel, t, lang, api, loadConversations, setHitl, notify, backgroundEvent } = ctx
  const s = store.getState()

  switch (msg.type) {
    // ── result: 运行完成 ──
    case 'result': {

      const subtype = msg.subtype || 'success'
      let isError = subtype !== 'success'
      let errorContent = ''
      let errorKind: 'auth' | 'budget' | 'turns' | 'generic' | undefined
      if (isError) {
        if (subtype === 'error_max_budget_usd') {
          errorContent = t('desktop.cowork.budgetLimit')
          errorKind = 'budget'
        } else if (subtype === 'error_max_turns') {
          errorContent = t('desktop.cowork.maxTurns')
          errorKind = 'turns'
        } else if (subtype === 'error_auth') {
          errorContent = msg.error || t('desktop.cowork.sessionExpired')
          errorKind = 'auth'
        } else {
          const rawError = msg.error || (msg.errors || []).join('; ') || 'Unknown error'
          errorContent = friendlyError(rawError, lang)
          // marker 携带 error_type 时认 budget（claudeAgent thrown 路径透传原始 marker）
          errorKind = errorActionKind(rawError) || 'generic'
        }
      }

      // Content-level error detection.
      // Observed: Claude Agent SDK CLI sometimes catches upstream errors
      // (auth failures, 5xx, overload) and emits them as normal text_delta
      // content instead of throwing, so the stream finishes with
      // subtype=success but m.content is the raw error string. Retroactively
      // flip these so the user sees friendly copy instead of the raw
      // English exception rendered as markdown.
      if (!isError) {
        const tail = s.sessions[targetSid]?.messages.find((x) => x.id === assistantMsgId)
        const existingContent = tail?.content || ''
        // 先认 deck 的 __CORTEX_ERR__ marker（携带 error_type）—— 否则 budget 的
        // 403 会被下面裸 403 正则误判成 auth，触发重登/登出循环（超预算用户尤甚）。
        if (errorActionKind(existingContent) === 'budget') {
          isError = true
          errorKind = 'budget'
          errorContent = friendlyError(existingContent, lang)
        } else if (
          /failed to authenticate|api error: 401|api error: 403|authentication_error|permission_error/i.test(existingContent)
          || /登录状态异常|登录已过期|please re-login/i.test(existingContent)
        ) {
          isError = true
          errorKind = 'auth'
          errorContent = t('desktop.cowork.sessionExpired')
        } else if (
          /api error: 5\d\d|internal server error|status\.claude\.com|server-side issue|overloaded_error|api error: 529/i.test(existingContent)
        ) {
          isError = true
          errorKind = 'generic'
          errorContent = friendlyError(existingContent, lang)
        }
      }

      // A dynamic Workflow is an independent background process. Its launching
      // turn can end while the workflow is still running, so only settle normal
      // in-progress todos when no background workflow remains. Errors always
      // settle them as failed.
      const workflowStillRunning = Object.values(store.getState().sessions[targetSid]?.workflows ?? {})
        .some((w) => w.status === 'running')

      // On error, ALWAYS overwrite content with our friendly text.
      s.updateMessages(targetSid, (prev) => prev.map((m) =>
        m.id === assistantMsgId
          ? {
            ...m,
            status: isError ? 'error' : 'success',
            content: isError ? errorContent : m.content,
            errorKind: isError ? errorKind : undefined,
            costUsd: msg.total_cost_usd,
            numTurns: msg.num_turns,
            // turn 结束兜底：把仍 running 的 tool_use（含 Agent 块）+ subagent
            // steps 标 completed，避免 turn 都完成了卡片还在转圈。
            contentBlocks: completeRunningToolBlocks(m.contentBlocks),
          }
          : m,
      ))
      s.stopProcess(targetSid)
      if (isError || !workflowStillRunning) {
        const todos = store.getState().sessions[targetSid]?.todos ?? []
        const settledTodos = settleInProgressTodos(todos, isError ? 'failed' : 'paused')
        if (settledTodos !== todos) s.updateSession(targetSid, { todos: settledTodos })
      }
      // Snapshot channelId before teardown — needed for the post-teardown
      // context-usage fetch below.
      const channelId = runtimes.peek(targetSid)?.channelId
      // A dynamic Workflow runs as a background task: after the agent launches it
      // (even fire-and-forget — ending the turn with just "已启动"), the SDK query
      // stays open (streaming-input mode) and keeps emitting THIS task's
      // task_progress/task_notification on this turn's channel *after* `result`.
      // When it completes, the SDK also WAKES the agent with a task-notification →
      // a RESUME turn on the same channel ("收到了 workflow 完成通知…"). The default
      // teardown here would cancel the listener → chip/panel freeze AND the resume
      // turn is lost from the live UI (it still lands in the transcript, so a
      // reload shows it — which is exactly the ghost we hunted). So while a
      // workflow is still running, keep the listener alive (defer its cleanup) and
      // flag the runtime so the resume turn opens its own bubble. The deferred
      // listener is drained by the resume turn's OWN `result` (this same branch,
      // by which point no workflow is running → teardown).
      if (workflowStillRunning) {
        runtimes.deferListenerCleanup(targetSid)
        const rt = runtimes.peek(targetSid)
        if (rt) rt.resumePending = true
      } else {
        runtimes.teardown(targetSid)
      }
      loadConversations()
      if (channelId && api?.getContextUsage) {
        api.getContextUsage(channelId).then((usage: any) => {
          if (usage) s.updateSession(targetSid, { contextUsage: usage })
        }).catch(() => {})
      }
      if (isError || !workflowStillRunning) {
        backgroundEvent({ kind: isError ? 'failed' : 'completed', sessionId: targetSid })
      }
      // Between-turn greedy drain. runtimes.teardown killed the debounce
      // timer that might have been scheduled during this turn — without an
      // explicit drain here, queued prompts orphan until the user types
      // again. Same pattern Claude Code uses (src/cli/print.ts
      // drainCommandQueue). Skip on error so a failed turn doesn't cascade.
      if (!isError) ctx.drainPending(targetSid)
      // If the user typed /compact but no compact_boundary fired this turn,
      // SDK treated it as a regular message (most commonly: not enough
      // messages to compact). Inline the notice right below the slash echo
      // so it reads as part of the transcript — a toast at the top of the
      // window felt disconnected from the command the user just ran.
      const sess = store.getState().sessions[targetSid]
      if (sess?.expectingCompact) {
        s.updateSession(targetSid, { expectingCompact: false })
        s.updateMessages(targetSid, (prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'system' as const,
          content: lang === 'zh'
            ? '↳ 压缩未执行：消息太少'
            : '↳ Compact skipped: too few messages',
          status: 'success' as const,
          createdAt: new Date(),
          isSlashCommandEcho: true,
        }])
      }
      break
    }

    // ── stream_event: Anthropic 流式事件 ──
    case 'stream_event': {
      const event = msg.event
      if (!event) break
      const isSubagent = !!msg.parent_tool_use_id

      // 流式状态推导（只对主 agent）
      if (!isSubagent) {
        if (event.type === 'message_start') s.setPhase(targetSid, 'thinking')
        else if (event.type === 'message_stop') s.setPhase(targetSid, 'thinking')
        else if (event.type === 'content_block_delta') {
          if (event.delta?.type === 'thinking_delta') s.setPhase(targetSid, 'thinking')
          else if (event.delta?.type === 'text_delta') s.setPhase(targetSid, 'responding')
        } else if (event.type === 'content_block_start' && event.content_block?.type === 'tool_use') {
          s.setPhase(targetSid, 'tool-use')
        }
      }

      // ── Sub-agent stream_event → 归到 Agent tool_use block 的 steps ──
      if (isSubagent) {
        const parentId = msg.parent_tool_use_id
        const findAgentBlock = (blocks: ContentBlock[]) => {
          for (let i = blocks.length - 1; i >= 0; i--) {
            const b = blocks[i]
            if (b.type === 'tool_use' && b.name === 'Agent' && b.id === parentId) return i
          }
          for (let i = blocks.length - 1; i >= 0; i--) {
            const b = blocks[i]
            if (b.type === 'tool_use' && b.name === 'Agent' && b.status === 'running') return i
          }
          return -1
        }

        switch (event.type) {
          case 'content_block_start': {
            const block = event.content_block
            if (block?.type === 'tool_use') {
              batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => {
                const blocks = [...prev]
                const idx = findAgentBlock(blocks)
                if (idx < 0) return blocks
                const b = blocks[idx] as ToolUseBlock
                const steps = b.steps || []
                if (steps.some(st => st.id === block.id)) return blocks
                blocks[idx] = { ...b, steps: [...steps, {
                  id: block.id, toolName: block.name, toolLabel: friendlyToolName(block.name),
                  toolArgs: {}, status: 'running' as const, startedAt: Date.now(),
                }] }
                return blocks
              } })
            }
            break
          }
          case 'content_block_delta': {
            const delta = event.delta
            if (delta?.type === 'input_json_delta' && delta.partial_json) {
              batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => {
                const blocks = [...prev]
                const idx = findAgentBlock(blocks)
                if (idx < 0) return blocks
                const b = blocks[idx] as ToolUseBlock
                const steps = [...(b.steps || [])]
                const si = steps.findLastIndex(st => st.status === 'running')
                if (si < 0) return blocks
                const step = steps[si]
                const raw = (step.toolArgs as any)?.__raw || ''
                const newRaw = raw + delta.partial_json
                let parsed: Record<string, unknown> = { __raw: newRaw }
                try { parsed = { ...JSON.parse(newRaw), __raw: newRaw } } catch {}
                steps[si] = { ...step, toolArgs: parsed }
                blocks[idx] = { ...b, steps }
                return blocks
              } })
            }
            break
          }
          case 'content_block_stop': {
            batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => {
              const blocks = [...prev]
              const idx = findAgentBlock(blocks)
              if (idx < 0) return blocks
              const b = blocks[idx] as ToolUseBlock
              const steps = [...(b.steps || [])]
              const si = steps.findLastIndex(st => st.status === 'running')
              if (si < 0) return blocks
              const step = steps[si]
              let finalArgs = step.toolArgs
              if ((finalArgs as any)?.__raw) {
                try { finalArgs = { ...JSON.parse((finalArgs as any).__raw), __raw: (finalArgs as any).__raw } } catch {}
              }
              steps[si] = { ...step, toolArgs: finalArgs, status: 'completed' as const, duration: Date.now() - step.startedAt }
              blocks[idx] = { ...b, steps }
              return blocks
            } })
            break
          }
        }
        break
      }

      // ── 主 agent stream_event ──
      switch (event.type) {
        case 'content_block_start': {
          const block = event.content_block
          if (block?.type === 'tool_use') {
            batchUpdate(targetSid, assistantMsgId, {
              blocks: (prev) => [...prev, {
                type: 'tool_use' as const,
                id: block.id,
                name: block.name,
                label: friendlyToolName(block.name),
                input: {},
                status: 'running' as const,
                startedAt: Date.now(),
              }],
            })
          }
          break
        }

        case 'content_block_delta': {
          const delta = event.delta
          if (delta?.type === 'text_delta' && delta.text) {
            const curLen = runtimes.peek(targetSid)?.contentLength || 0
            runtimes.get(targetSid).contentLength = curLen + delta.text.length
            appendText(targetSid, assistantMsgId, delta.text)
          }
          if (delta?.type === 'input_json_delta' && delta.partial_json) {
            batchUpdate(targetSid, assistantMsgId, {
              blocks: (prev) => {
                const blocks = [...prev]
                for (let i = blocks.length - 1; i >= 0; i--) {
                  const b = blocks[i]
                  if (b.type === 'tool_use' && b.status === 'running') {
                    const raw = (b.input as any).__raw || ''
                    const newRaw = raw + delta.partial_json
                    let parsed: Record<string, unknown> = { __raw: newRaw }
                    try { parsed = { ...JSON.parse(newRaw), __raw: newRaw } } catch {}
                    blocks[i] = { ...b, input: parsed }
                    break
                  }
                }
                return blocks
              },
            })
          }
          break
        }

        case 'content_block_stop': {
          const currentBlocks = store.getState().sessions[targetSid]?.messages.find(m => m.id === assistantMsgId)?.contentBlocks || []
          for (let i = currentBlocks.length - 1; i >= 0; i--) {
            const b = currentBlocks[i]
            if (b.type === 'tool_use' && b.status === 'running') {
              if (b.name === 'TaskUpdate') {
                // TaskUpdate 的全部字段在 input 里，结果不需要等
                try {
                  const raw = (b.input as any).__raw || JSON.stringify(b.input)
                  applyTaskTool(store, targetSid, 'TaskUpdate', JSON.parse(raw))
                } catch (e) { console.warn('[TaskUpdate] parse error', e) }
              }
              break
            }
          }
          batchUpdate(targetSid, assistantMsgId, {
            blocks: (prev) => {
              const blocks = [...prev]
              for (let i = blocks.length - 1; i >= 0; i--) {
                const b = blocks[i]
                // Agent 块例外：content_block_stop 只代表它的 input（description/prompt）
                // 流完，subagent 还没跑。提前标 completed 会让卡片一渲染就"完成"（实测
                // bug）。Agent 块只在自己的 tool_result 到达时才完成（见 user case）。
                if (b.type === 'tool_use' && b.status === 'running' && b.name !== 'Agent') {
                  let finalInput = b.input
                  if ((finalInput as any)?.__raw) {
                    try { finalInput = { ...JSON.parse((finalInput as any).__raw), __raw: (finalInput as any).__raw } } catch {}
                  }
                  const frozenLabel = computeToolLabel(b.name, finalInput)
                  blocks[i] = { ...b, input: finalInput, label: frozenLabel, status: 'completed' as const, duration: Date.now() - b.startedAt }
                  break
                }
              }
              return blocks
            },
          })
          break
        }

        case 'message_stop':
          break
      }
      break
    }

    // ── assistant: 完整的 assistant 消息 ──
    case 'assistant': {
      const parentToolId = msg.parent_tool_use_id
      const content = msg.message?.content

      if (parentToolId && Array.isArray(content)) {
        const toolBlocks = content.filter((b: any) => b.type === 'tool_use')
        if (toolBlocks.length > 0) {
          batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => {
            const blocks = [...prev]
            let idx = blocks.findLastIndex(b => b.type === 'tool_use' && b.name === 'Agent' && b.id === parentToolId)
            if (idx < 0) idx = blocks.findLastIndex(b => b.type === 'tool_use' && b.name === 'Agent' && b.status === 'running')
            if (idx < 0) return blocks
            const b = blocks[idx] as ToolUseBlock
            const steps = [...(b.steps || [])]
            for (const tb of toolBlocks) {
              const existingIdx = steps.findIndex(st => st.id === tb.id)
              if (existingIdx >= 0) {
                steps[existingIdx] = { ...steps[existingIdx], toolArgs: tb.input }
              } else {
                steps.push({
                  id: tb.id, toolName: tb.name, toolLabel: friendlyToolName(tb.name),
                  toolArgs: tb.input, status: 'running' as const, startedAt: Date.now(),
                })
              }
            }
            blocks[idx] = { ...b, steps }
            return blocks
          } })
        }
        break
      }

      if (Array.isArray(content)) {
        const textParts = content.filter((b: any) => b.type === 'text').map((b: any) => b.text)
        if (textParts.length > 0) {
          const fullText = textParts.join('\n')
          s.updateMessages(targetSid, (prev) => prev.map((m) => {
            if (m.id !== assistantMsgId) return m
            if (m.content.length >= fullText.length) return m
            runtimes.get(targetSid).contentLength = fullText.length
            return { ...m, content: fullText }
          }))
        }
        const thinkingParts = content.filter((b: any) => b.type === 'thinking').map((b: any) => b.thinking)
        if (thinkingParts.length > 0) {
          const thinkingText = thinkingParts.join('\n')
          batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => {
            const hasThinking = prev.some(b => b.type === 'thinking')
            if (hasThinking) return prev
            return [{ type: 'thinking' as const, thinking: thinkingText }, ...prev]
          } })
        }
      }
      break
    }

    // ── user: SDK 的 user 消息（含 tool_result）──
    case 'user': {
      const parentToolId = msg.parent_tool_use_id
      const content = msg.message?.content
      if (!Array.isArray(content)) break

      for (const block of content) {
        if (block.type !== 'tool_result' || !block.tool_use_id) continue

        const toolId = block.tool_use_id
        let output = ''
        if (typeof block.content === 'string') {
          output = block.content
        } else if (Array.isArray(block.content)) {
          output = block.content
            .filter((c: any) => c.type === 'text')
            .map((c: any) => c.text)
            .join('\n')
        }
        const truncated = output.length > 2000 ? output.slice(0, 2000) + '\n...(truncated)' : output

        if (parentToolId) {
          batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => {
            const blocks = [...prev]
            let idx = blocks.findLastIndex(b => b.type === 'tool_use' && b.name === 'Agent' && b.id === parentToolId)
            if (idx < 0) idx = blocks.findLastIndex(b => b.type === 'tool_use' && b.name === 'Agent' && b.status === 'running')
            if (idx < 0) return blocks
            const b = blocks[idx] as ToolUseBlock
            const steps = (b.steps || []).map(st =>
              st.id === toolId
                ? { ...st, status: 'completed' as const, duration: Date.now() - st.startedAt, output: truncated }
                : st,
            )
            blocks[idx] = { ...b, steps }
            return blocks
          } })
        } else {
          if (!output) continue
          // Task 工具结果 → 累积 todos。TaskCreate 的 id 只在结果里，TaskList
          // 结果是全量快照 —— 都得在 tool_result 阶段处理（TaskUpdate 已在
          // content_block_stop 用 input 处理过）。
          const decodedBlocks = store.getState().sessions[targetSid]?.messages.find(m => m.id === assistantMsgId)?.contentBlocks || []
          const taskBlock = decodedBlocks.find(b => b.type === 'tool_use' && b.id === toolId) as ToolUseBlock | undefined
          if (taskBlock && (taskBlock.name === 'TaskCreate' || taskBlock.name === 'TaskList')) {
            applyTaskTool(store, targetSid, taskBlock.name, taskBlock.input, output)
          }
          batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => {
            const blocks = [...prev]
            for (let i = blocks.length - 1; i >= 0; i--) {
              const b = blocks[i]
              if (b.type === 'tool_use' && b.id === toolId) {
                // tool_result 到达 = 这个工具真正完成。Agent 块靠这里收尾（它在
                // content_block_stop 被故意跳过、不提前完成，见上面 name!=='Agent'），
                // 否则 subagent 跑完了卡片还一直转圈。普通工具已在 stop 标过，幂等。
                blocks[i] = { ...b, output: truncated, status: 'completed' as const, duration: (b as ToolUseBlock).duration ?? (Date.now() - b.startedAt) }
                break
              }
            }
            return blocks
          } })
        }
      }
      break
    }

    // ── tool_progress ──
    case 'tool_progress':
      batchUpdate(targetSid, assistantMsgId, {
        blocks: (prev) => applySubagentRetryProgress(prev, msg),
      })
      break

    // ── prompt_suggestion ──
    case 'prompt_suggestion': {
      const text = msg.suggestion || ''
      if (text) {
        s.updateMessages(targetSid, (prev) => prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, promptSuggestions: [...(m.promptSuggestions || []), text] }
            : m,
        ))
      }
      break
    }

    // ── ask_user_question ──
    case 'ask_user_question': {
      console.log('[HITL] ask_user_question received:', JSON.stringify(msg).slice(0, 200))
      const questions = msg.questions || []
      const askId = msg.askId || crypto.randomUUID()

      const fields = questions.map((q: any) => {
        const hasOptions = q.options && q.options.length > 0
        return {
          name: q.question,
          description: JSON.stringify({
            label: q.question,
            header: q.header,
            multiSelect: q.multiSelect || false,
            ui: hasOptions ? 'buttons' : 'text',
            options: hasOptions ? q.options.map((o: any) => typeof o === 'string' ? o : o.label) : undefined,
            optionDetails: hasOptions ? q.options.map((o: any) => ({ label: typeof o === 'string' ? o : o.label, description: o.description || '' })) : undefined,
          }),
          field_type: 'string',
          value: '',
        }
      })

      if (fields.length === 0) {
        fields.push({
          name: 'answer',
          description: JSON.stringify({ label: t('desktop.cowork.hitlFallback'), ui: 'text' }),
          field_type: 'string',
          value: '',
        })
      }

      const rawQuestions = questions.map((q: any) => ({
        question: q.question,
        header: q.header,
        multiSelect: q.multiSelect || false,
        options: (q.options || []).map((o: any) => ({
          label: typeof o === 'string' ? o : o.label,
          description: typeof o === 'string' ? '' : (o.description || ''),
          preview: o.preview,
        })),
      }))

      const newHitl: HITLState = {
        requirements: [{ id: askId, user_input_schema: fields }],
        runId: askId,
        sessionId: targetSid,
        rawQuestions,
      }

      const nonce = hitlNonce(newHitl)
      const cached = cachedHitlDecision(nonce)
      if (cached) {
        api.respondAskUser(newHitl.runId, cached)
        break
      }
      const activeHitl = store.getState().hitl
      const dupActive = activeHitl && hitlNonce(activeHitl) === nonce
      const dupQueued = hitlQueueRef.current.some((q) => q.nonce === nonce)
      if (dupActive || dupQueued) break

      if (!activeHitl && targetSid === store.getState().currentSessionId) {
        setHitl(newHitl)
      } else {
        hitlQueueRef.current.push({ hitl: newHitl, nonce })
      }
      backgroundEvent({ kind: 'input-needed', sessionId: targetSid })
      break
    }

    case 'ask_user_question_resolved': {
      const askId = typeof msg.askId === 'string' ? msg.askId : ''
      if (!askId) break

      const active = store.getState().hitl
      const queued = hitlQueueRef.current.find(({ hitl }) => hitl.runId === askId)?.hitl
      const resolved = active?.runId === askId ? active : queued
      if (resolved && msg.answers && typeof msg.answers === 'object') {
        cacheHitlDecision(hitlNonce(resolved), msg.answers)
      }

      const resolution = resolveHitlPrompt(
        active,
        hitlQueueRef.current,
        askId,
        store.getState().currentSessionId,
      )
      hitlQueueRef.current = resolution.queue
      if (resolution.active !== active) setHitl(resolution.active)
      break
    }

    // ── tool_audit ──
    case 'tool_audit':
      break

    // ── system ──
    case 'system': {
      switch (msg.subtype) {
        case 'init':
          console.log('[Cowork] SDK init, model:', msg.model, 'tools:', msg.tools?.length, 'mcp:', msg.mcp_servers?.length)
          if (msg.tools) {
            s.updateSession(targetSid, { loadedTools: msg.tools.map((tool: any) => typeof tool === 'string' ? tool : tool.name || tool.id || '') })
          }
          break
        case 'files_persisted': {
          const files = (msg.files || []).map((f: any) => f.filename || f)
          if (files.length > 0) {
            s.updateMessages(targetSid, (prev) => prev.map((m) =>
              m.id === assistantMsgId ? { ...m, filesPersisted: [...(m.filesPersisted || []), ...files] } : m,
            ))
          }
          break
        }
        case 'api_retry':
          console.warn('[Cowork] API retry attempt', msg.attempt, '/', msg.max_retries,
            'status:', msg.error_status, 'error:', msg.error, 'delay:', msg.retry_delay_ms)
          if (msg.attempt >= 2) {
            notify('warning', `${t('desktop.cowork.apiRetry')} (${msg.attempt}/${msg.max_retries})...`)
          }
          break
        case 'session_reset':
          s.updateMessages(targetSid, (prev) => [...prev, {
            id: crypto.randomUUID(), role: 'system' as const, content: msg.message || '会话已重置',
            status: 'success' as const, createdAt: new Date(), isCompactSummary: true,
          }])
          break
        case 'status':
          // compacting → set session flag; banner + pending-queue gate consume it.
          // Don't append an inline message — the old "*上下文已压缩,继续执行中*"
          // text fired at the START of compaction (misleading: SDK hadn't actually
          // finished compacting yet). The real summary now lives in the banner,
          // keyed off lastCompactSummary written by compact_boundary.
          if (msg.status === 'compacting') {
            console.log('[Cowork] Context compacting...')
            s.updateSession(targetSid, {
              compacting: true,
              compactionStartedAt: store.getState().sessions[targetSid]?.compactionStartedAt ?? Date.now(),
            })
          } else if (msg.status === 'requesting') {
            s.setPhase(targetSid, 'requesting')
          } else if (msg.status === null) {
            // Safety: status cleared without a compact_boundary (e.g. failed
            // compact, not-enough-messages, PROMPT_TOO_LONG). Release the
            // drain gate so queued prompts don't hang.
            if (store.getState().sessions[targetSid]?.compacting) {
              s.updateSession(targetSid, {
                compacting: false,
                compactionStartedAt: null,
              })
              ctx.drainPending(targetSid)
            }
            if (msg.compact_result === 'failed') {
              console.warn('[Cowork] compaction failed:', msg.compact_error)
              // Same inline-note treatment as the "expectingCompact with no
              // boundary" branch above: surfaces the rejection reason in the
              // transcript rather than a top-window toast. `expectingCompact`
              // may or may not be set here (SDK path ordering varies); clear
              // it regardless so result doesn't double-warn.
              s.updateSession(targetSid, { expectingCompact: false })
              s.updateMessages(targetSid, (prev) => [...prev, {
                id: crypto.randomUUID(),
                role: 'system' as const,
                content: lang === 'zh'
                  ? `↳ 压缩失败：${msg.compact_error || '消息太少或请求过长'}`
                  : `↳ Compact failed: ${msg.compact_error || 'too few messages or prompt too long'}`,
                status: 'success' as const,
                createdAt: new Date(),
                isSlashCommandEcho: true,
              }])
            }
          }
          break
        case 'compact_boundary': {
          const meta = msg.compact_metadata || {}
          console.log('[Cowork] compact_boundary', meta)
          s.updateSession(targetSid, {
            compacting: false,
            compactionStartedAt: null,
            expectingCompact: false,
            lastCompactSummary: {
              preTokens: meta.pre_tokens ?? 0,
              postTokens: meta.post_tokens,
              durationMs: meta.duration_ms,
            },
          })
          ctx.drainPending(targetSid)
          break
        }
        case 'task_started': {
          const toolUseId = msg.tool_use_id
          if (toolUseId) {
            const isWorkflow = msg.task_type === 'local_workflow'
            if (isWorkflow) {
              const run: WorkflowRun = {
                taskId: msg.task_id || '', name: msg.workflow_name || 'Workflow',
                description: (msg as any).description, status: 'running',
                phases: [], agents: [], startedAt: Date.now(),
              }
              // Link the tool_use block to its run by taskId only — the chip
              // renders from session.workflows[taskId] (single source of truth),
              // not from block state. task_started fires within the launching
              // turn, so assistantMsgId is the correct owner here.
              batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => prev.map(b =>
                b.type === 'tool_use' && b.id === toolUseId ? { ...b, taskId: run.taskId } : b,
              ) })
              // session-level registry drives BOTH the inline chip and the panel.
              upsertSessionWorkflow(store, targetSid, run.taskId, run)
            } else {
              // Agent (Task-tool subagent) block: attach the task_id for step aggregation.
              batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => prev.map(b =>
                b.type === 'tool_use' && b.name === 'Agent' && b.id === toolUseId
                  ? { ...b, taskId: msg.task_id || '' } : b,
              ) })
            }
          }
          break
        }
        case 'task_progress': {
          if (!msg.task_id) break
          // usage advances every heartbeat tick; phases/agents only when the
          // workflow_progress snapshot rides along (it's absent on all-running ticks).
          const usage = (msg as any).usage
          const delta: Partial<WorkflowRun> = {}
          if (typeof usage?.total_tokens === 'number') delta.totalTokens = usage.total_tokens
          if (typeof usage?.duration_ms === 'number') delta.durationMs = usage.duration_ms
          const wp = decodeWorkflowProgress(msg)
          if (wp) { delta.phases = wp.phases; delta.agents = wp.agents }
          if (Object.keys(delta).length === 0) break
          // Session-scoped registry only — keyed by taskId, turn-independent.
          upsertSessionWorkflow(store, targetSid, msg.task_id, delta)
          break
        }
        case 'task_updated': {
          // patch.status may flip the workflow to a terminal state (completed /
          // failed / stopped / killed). Non-terminal patches (running/paused) are
          // ignored here — task_progress carries the live tokens/agents.
          const patch = (msg as any).patch || {}
          const status = msg.task_id ? terminalWorkflowStatus(patch.status) : null
          if (msg.task_id && status && store.getState().sessions[targetSid]?.workflows?.[msg.task_id]) {
            settleWorkflowTerminal(store, targetSid, msg.task_id, status)
          }
          break
        }
        case 'task_notification': {
          // Workflow terminal event: final status (completed | failed | stopped)
          // + summary + output file. task_notification IS terminal, so settle
          // unconditionally to a terminal status (never leaves the chip spinning).
          if (store.getState().sessions[targetSid]?.workflows?.[msg.task_id]) {
            const status = terminalWorkflowStatus(msg.status) ?? 'stopped'
            settleWorkflowTerminal(store, targetSid, msg.task_id, status, {
              summary: msg.summary, outputFile: (msg as any).output_file,
            })
            // NOTE: do NOT release the deferred listener here. This terminal
            // notification is immediately followed by the SDK-woken RESUME turn on
            // the same channel; tearing the listener down now would drop it from
            // the live UI. The resume turn's own `result` (workflow no longer
            // running) does the teardown.
          }
          // Agent (Task-tool subagent) block: close out any still-running steps.
          batchUpdate(targetSid, assistantMsgId, { blocks: (prev) => prev.map(b => {
            if (b.type !== 'tool_use' || b.name !== 'Agent' || b.taskId !== msg.task_id) return b
            const steps = (b.steps || []).map(st =>
              st.status === 'running' ? { ...st, status: 'completed' as const, duration: Date.now() - st.startedAt } : st,
            )
            return { ...b, steps }
          }) })
          break
        }
        case 'session_state_changed':
          console.log('[Cowork] session state:', msg.state)
          if (msg.state === 'idle') {
            const channelId = runtimes.peek(targetSid)?.channelId
            if (channelId && api?.getContextUsage) {
              api.getContextUsage(channelId).then((usage: any) => {
                if (usage) s.updateSession(targetSid, { contextUsage: usage })
              }).catch(() => {})
            }
          }
          break
      }
      break
    }

    // ── compact_summary: synthetic event from main-side PostCompact hook,
    //    carrying the real summary text (SDK's native stream only surfaces
    //    compact_boundary metadata; the summary itself is produced by an
    //    internal forked agent that never hits our stream). Rendered via the
    //    existing isCompactSummary fold path.
    case 'compact_summary': {
      const summary = String(msg.summary || '').trim()
      if (!summary) break
      s.updateMessages(targetSid, (prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'system' as const,
        content: summary,
        status: 'success' as const,
        createdAt: new Date(),
        isCompactSummary: true,
      }])
      break
    }

    // ── tool_use_summary ──
    // Tag the specific tool_use blocks this summary covers (preceding_tool_use_ids)
    // rather than the whole message. A streaming message accumulates multiple
    // tool runs, each with its own summary; writing a message-level field made
    // every ToolGroup in the message render the LATEST summary — so earlier,
    // already-completed groups appeared to "change" to match the newest run.
    // Per-block, each grouped run keeps its own title (groupContentBlocks picks
    // it up). Falls back to message-level only when ids are absent.
    case 'tool_use_summary': {
      const summary = msg.summary as string
      const ids: string[] = Array.isArray((msg as { preceding_tool_use_ids?: unknown }).preceding_tool_use_ids)
        ? ((msg as { preceding_tool_use_ids: string[] }).preceding_tool_use_ids)
        : []
      if (summary) {
        s.updateMessages(targetSid, (prev) => prev.map((m) => {
          if (m.id !== assistantMsgId) return m
          if (ids.length && m.contentBlocks) {
            const idSet = new Set(ids)
            let changed = false
            const contentBlocks = m.contentBlocks.map((b) => {
              if (b.type === 'tool_use' && idSet.has(b.id)) { changed = true; return { ...b, toolUseSummary: summary } }
              return b
            })
            return changed ? { ...m, contentBlocks } : m
          }
          return { ...m, toolUseSummary: summary } // legacy: no ids → message-level
        }))
      }
      break
    }

    default:
      console.log('[Cowork] unhandled msg type:', msg.type)
  }
}


// ── 只要进度的消费方(Consult 模式)────────────────────────────────────
/**
 * Consult 的办理页不渲染对话,只需要三样:任务清单(工序进度)、待答问题、
 * 是否结束。构造完整 `StreamDecoderContext` 对它是负担(消息追加、工具名
 * 本地化、runtime 管理全都用不上),但 Task 工具那套文本解析又**绝不能重写**
 * —— 它的坑(id 正则、TaskList 行锚定、按 id 而非到达顺序排)都在上面注释里。
 *
 * 所以给出这个窄入口:内部复用 applyTaskTool,只额外认 ask_user_question 与
 * result 两类事件。Cowork 的路径一行未变。
 */
export function decodeProgressMessage(
  msg: any,
  sid: string,
  ctx: {
    store: any
    onHitl: (hitl: HITLState | null) => void
    /**
     * 这个提问已经了结了 —— **不是必然由用户在本界面点的**。
     *
     * 主进程在三条路上都发 `ask_user_question_resolved`:用户作答、超时自动提交
     * (HITL_TIMEOUT)、以及会话 abort 时的 cancelled。后两条这边收不到任何别的
     * 信号,所以不处理的话问题卡就永远留在界面上 —— 而那个 askId 主进程早已
     * settle,用户再点「提交」不会有任何反应,案卷也一直停在「等你确认」,桌面
     * 宠物一直在催。必答,不给可选:漏掉不会报错,只会静默留下一张点不动的卡。
     */
    onHitlResolved: (askId: string) => void
    onFinished: (error?: string) => void
    /** 调用方持有的 tool_use 拼装/配对表(见下方说明),跨消息保持。
     *  键先是流式 block index,拼完改挂 tool_use_id 等 tool_result。 */
    pendingTools: Map<string, { name: string; input: any; id?: string; raw?: string }>
    /** 办理过程:`running` = 此刻在调的工具,`summary` = SDK 给的一段人话总结。
     *  进度消费方不渲染消息流,这是它唯一能拿到「在干什么」的地方。 */
    onActivity?: (a: { kind: 'running' | 'summary'; toolName?: string; input?: any; text?: string }) => void
  },
): void {
  const { store, onHitl, onHitlResolved, onFinished, pendingTools, onActivity } = ctx

  if (msg.type === 'tool_audit') {
    onActivity?.({ kind: 'running', toolName: msg.toolName, input: msg.toolInput })
    return
  }
  if (msg.type === 'tool_use_summary' && typeof msg.summary === 'string' && msg.summary) {
    onActivity?.({ kind: 'summary', text: msg.summary })
    return
  }

  if (msg.type === 'result') {
    const subtype = msg.subtype || 'success'
    onFinished(subtype === 'success' ? undefined : String(subtype))
    return
  }

  if (msg.type === 'ask_user_question') {
    const questions = msg.questions || []
    const askId = msg.askId || crypto.randomUUID()
    onHitl({
      requirements: [{ id: askId, user_input_schema: [] }],
      runId: askId,
      sessionId: sid,
      rawQuestions: questions.map((q: any) => ({
        question: q.question,
        header: q.header,
        multiSelect: q.multiSelect || false,
        options: (q.options || []).map((o: any) => ({
          label: typeof o === 'string' ? o : o.label,
          description: typeof o === 'string' ? '' : (o.description || ''),
          preview: o.preview,
        })),
      })),
    })
    return
  }

  if (msg.type === 'ask_user_question_resolved') {
    // 带上 askId 交给调用方比对:同一会话里可能刚发出下一个提问,拿旧 askId
    // 无条件清掉会把新问题一起抹了。调用方持有当前 hitl,由它判断。
    if (typeof msg.askId === 'string' && msg.askId) onHitlResolved(msg.askId)
    return
  }

  // ── 流式 tool_use:content_block_start 给 name/id,input 靠 input_json_delta
  // 逐片拼,content_block_stop 才完整。**必须自己拼**:完整解码路径是从 store 里
  // 已渲染的 contentBlocks 反查,而进度消费方不渲染消息、没有那份 blocks。
  if (msg.type === 'stream_event') {
    const ev = msg.event
    if (ev?.type === 'content_block_start' && ev.content_block?.type === 'tool_use') {
      const cb = ev.content_block
      if (typeof cb.name === 'string' && cb.name.startsWith('Task') && cb.id) {
        pendingTools.set(String(ev.index), { name: cb.name, input: {}, id: cb.id, raw: '' })
      }
      return
    }
    if (ev?.type === 'content_block_delta' && ev.delta?.type === 'input_json_delta') {
      const cur = pendingTools.get(String(ev.index))
      if (cur) cur.raw = (cur.raw ?? '') + (ev.delta.partial_json ?? '')
      return
    }
    if (ev?.type === 'content_block_stop') {
      const cur = pendingTools.get(String(ev.index))
      if (!cur) return
      pendingTools.delete(String(ev.index))
      try { cur.input = JSON.parse(cur.raw || '{}') } catch { cur.input = {} }
      if (cur.name === 'TaskUpdate') {
        // TaskUpdate 全部字段在 input 里,不必等结果
        applyTaskTool(store, sid, cur.name, normalizeTaskUpdate(cur.input))
      } else if (cur.id) {
        // TaskCreate / TaskList 的 id 或快照只在结果文本里 —— 挂到 tool_use_id 等结果
        pendingTools.set(cur.id, cur)
      }
      return
    }
    return
  }

  // 非流式 assistant 消息(部分路径会整块下发)—— 同样记录待配对。
  // TaskCreate 的 id 只在 tool_result 里,不能在这里凭 input 造条目:两条路都会
  // 收到同一次调用,自造 id 会和结果里的真 id 各建一条(实测 7 次调用变 14 条)。
  if (msg.type === 'assistant') {
    const blocks = msg?.message?.content
    if (Array.isArray(blocks)) {
      for (const b of blocks) {
        if (b?.type === 'tool_use' && typeof b.name === 'string' && b.name.startsWith('Task') && b.id) {
          if (b.name === 'TaskUpdate') applyTaskTool(store, sid, b.name, normalizeTaskUpdate(b.input))
          else pendingTools.set(b.id, { name: b.name, input: b.input })
        }
      }
    }
    return
  }

  // user 消息:tool_result 回来 → 配对后交给共用的 Task 解析
  // (TaskCreate 的 id 只在结果文本里,TaskList 的结果是全量快照)
  if (msg.type === 'user') {
    const content = msg?.message?.content
    if (!Array.isArray(content)) return
    for (const block of content) {
      if (block?.type !== 'tool_result' || !block.tool_use_id) continue
      const pending = pendingTools.get(block.tool_use_id)
      if (!pending) continue
      pendingTools.delete(block.tool_use_id)
      if (pending.name !== 'TaskCreate' && pending.name !== 'TaskList') continue
      const output = typeof block.content === 'string'
        ? block.content
        : Array.isArray(block.content)
          ? block.content.map((c: any) => (typeof c === 'string' ? c : c?.text ?? '')).join('')
          : ''
      if (output) applyTaskTool(store, sid, pending.name, pending.input, output)
    }
  }
}
